import React from 'react';
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FILTER_OPTIONS, FILTER_LABELS } from "@/types/filter-options";

interface LifestyleData {
  smoker?: string;
  drinker?: string;
  piercings?: string;
  tattoos?: string;
  children?: string;
}

interface SharedLifestyleSectionProps {
  data: LifestyleData;
  updateData: (field: string, value: string) => void;
  isOnboarding?: boolean;
  isEditing?: boolean;
}

const SharedLifestyleSection: React.FC<SharedLifestyleSectionProps> = ({
  data,
  updateData,
  isOnboarding = false,
  isEditing = true
}) => {
  const labelClass = isOnboarding ? "text-white" : "text-white/90 text-sm";
  const selectClass = isOnboarding 
    ? "bg-white/5 border-white/10 text-white" 
    : "bg-black/20 border-white/20 text-white hover:bg-black/30 transition-colors";
  const contentClass = isOnboarding 
    ? "bg-black/90 border-white/20 text-white" 
    : "bg-black/95 border-white/20 backdrop-blur-xl";
  const itemClass = isOnboarding 
    ? "text-white hover:bg-white/10 hover:text-white focus:bg-white/10 focus:text-white" 
    : "text-white hover:bg-white/10 focus:bg-white/10";

  // Se não está editando e não é onboarding, mostrar apenas valores como texto
  if (!isEditing && !isOnboarding) {
    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-white font-medium text-lg mb-2">Fumante</h3>
          {data.smoker ? (
            <p className="text-white/80">
              {FILTER_LABELS.smoker[data.smoker as keyof typeof FILTER_LABELS.smoker] || data.smoker}
            </p>
          ) : (
            <p className="text-red-400">Por favor, completar o perfil.</p>
          )}
        </div>
        
        <div>
          <h3 className="text-white font-medium text-lg mb-2">Bebidas</h3>
          {data.drinker ? (
            <p className="text-white/80">
              {FILTER_LABELS.drinker[data.drinker as keyof typeof FILTER_LABELS.drinker] || data.drinker}
            </p>
          ) : (
            <p className="text-red-400">Por favor, completar o perfil.</p>
          )}
        </div>
        
        <div>
          <h3 className="text-white font-medium text-lg mb-2">Piercings</h3>
          {data.piercings ? (
            <p className="text-white/80">
              {FILTER_LABELS.piercings[data.piercings as keyof typeof FILTER_LABELS.piercings] || data.piercings}
            </p>
          ) : (
            <p className="text-red-400">Por favor, completar o perfil.</p>
          )}
        </div>
        
        <div>
          <h3 className="text-white font-medium text-lg mb-2">Tatuagens</h3>
          {data.tattoos ? (
            <p className="text-white/80">
              {FILTER_LABELS.tattoos[data.tattoos as keyof typeof FILTER_LABELS.tattoos] || data.tattoos}
            </p>
          ) : (
            <p className="text-red-400">Por favor, completar o perfil.</p>
          )}
        </div>
        
        <div>
          <h3 className="text-white font-medium text-lg mb-2">Filhos</h3>
          {data.children ? (
            <p className="text-white/80">
              {FILTER_LABELS.children[data.children as keyof typeof FILTER_LABELS.children] || data.children}
            </p>
          ) : (
            <p className="text-red-400">Por favor, completar o perfil.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!isOnboarding && !isEditing && (
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
            <span className="text-lg">🏃</span>
          </div>
          <h3 className="text-white font-medium">Estilo de Vida</h3>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="smoker" className={labelClass}>Fumante</Label>
          <Select value={data.smoker || ''} onValueChange={(value) => updateData('smoker', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.smoker.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.smoker[option as keyof typeof FILTER_LABELS.smoker]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="drinker" className={labelClass}>Bebidas</Label>
          <Select value={data.drinker || ''} onValueChange={(value) => updateData('drinker', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.drinker.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.drinker[option as keyof typeof FILTER_LABELS.drinker]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="piercings" className={labelClass}>Piercings</Label>
          <Select value={data.piercings || ''} onValueChange={(value) => updateData('piercings', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.piercings.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.piercings[option as keyof typeof FILTER_LABELS.piercings]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tattoos" className={labelClass}>Tatuagens</Label>
          <Select value={data.tattoos || ''} onValueChange={(value) => updateData('tattoos', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.tattoos.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.tattoos[option as keyof typeof FILTER_LABELS.tattoos]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="children" className={labelClass}>Filhos</Label>
          <Select value={data.children || ''} onValueChange={(value) => updateData('children', value)}>
            <SelectTrigger className={selectClass}>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent className={contentClass}>
              {FILTER_OPTIONS.children.map(option => (
                <SelectItem 
                  key={option} 
                  value={option}
                  className={itemClass}
                >
                  {FILTER_LABELS.children[option as keyof typeof FILTER_LABELS.children]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default SharedLifestyleSection;